import { useMemo, useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { streamers } from '@/mocks/content';

export default function Streamers() {
  const [liveOnly, setLiveOnly] = useState(false);

  const list = useMemo(
    () => (liveOnly ? streamers.filter((streamer) => streamer.live) : streamers),
    [liveOnly],
  );

  return (
    <section id="streamers" className="py-12 md:py-16 scroll-mt-20">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between dv-reveal">
          <SectionHeading
            kicker="福利主播"
            title="福利主播榜单：不同赛道的主播，一张榜单看完"
            desc="按综合热度排列，包含综艺娱乐、音乐电台、影视解说、美食、游戏赛事与户外旅行等方向。开播状态会标注出来，挑几位固定追更更容易形成自己的观看节奏。"
          />

          <button
            type="button"
            onClick={() => setLiveOnly((prev) => !prev)}
            aria-pressed={liveOnly}
            className={`inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
              liveOnly
                ? 'bg-secondary-500 text-background-50'
                : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300 hover:text-secondary-800'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${liveOnly ? 'bg-background-50' : 'bg-secondary-500'}`} />
            只看正在直播
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((streamer, index) => (
            <article
              key={streamer.id}
              className={`relative flex flex-col rounded-lg border p-5 transition-transform duration-300 hover:-translate-y-1 ${
                index === 0 ? 'border-accent-300 bg-accent-50' : 'border-background-200 bg-background-50'
              }`}
            >
              <span
                className={`absolute top-4 right-4 font-heading text-2xl font-black leading-none ${
                  index === 0 ? 'text-accent-500' : 'text-background-300'
                }`}
              >
                {String(index + 1).padStart(2, '0')}
              </span>

              <div className="flex items-center gap-3.5">
                <div className="relative w-14 h-14 md:w-16 md:h-16 flex-none">
                  <img
                    src={streamer.avatar}
                    alt={`${streamer.name} 主播头像`}
                    title={`${streamer.name} 福利主播`}
                    className="w-full h-full rounded-full object-cover object-top border-2 border-background-100"
                  />
                  {streamer.live ? (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-medium text-background-50 whitespace-nowrap">
                      直播中
                    </span>
                  ) : (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 rounded-full bg-background-300 px-2 py-0.5 text-[10px] text-foreground-700 whitespace-nowrap">
                      未开播
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 truncate">
                    {streamer.name}
                  </h3>
                  <span className="mt-1 inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                    {streamer.category}
                  </span>
                </div>
              </div>

              <p className="mt-3.5 text-xs md:text-sm leading-relaxed text-foreground-600 line-clamp-2">
                {streamer.tagline}
              </p>

              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span>综合热度</span>
                  <span className="font-semibold text-foreground-800">{streamer.heat}</span>
                </div>
                <div className="mt-1.5 h-1.5 w-full rounded-full bg-background-200">
                  <div
                    className={`h-full rounded-full ${index === 0 ? 'bg-accent-500' : 'bg-primary-500'}`}
                    style={{ width: `${streamer.heat}%` }}
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-1.5">
                {streamer.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded bg-background-100 px-2 py-0.5 text-[11px] text-foreground-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-group-line" /> {streamer.followers} 关注
                </span>
                <span className="inline-flex items-center gap-1 text-primary-700">
                  <i className="ri-live-line" /> 进入主页
                </span>
              </div>
            </article>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 py-12 text-center">
            <i className="ri-live-line text-4xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-700">当前没有正在直播的主播</p>
            <button
              type="button"
              onClick={() => setLiveOnly(false)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600 cursor-pointer"
            >
              查看全部主播
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}